import { useMemo, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { ClearInputButton, SearchInput } from "../controls";
import {
  fieldLabelClassName,
  textInputClassName,
  IHomeTileConfigFormFields,
  PERSONA_OPERATORS,
  matchesSearch,
  sectionLabelClassName,
} from "../interface";

export const PersonaList = ({
  personas,
  isLoading,
  query,
  onPick,
}: {
  personas: string[];
  isLoading: boolean;
  query: string;
  onPick: (persona: string) => void;
}) => {
  if (isLoading) {
    return (
      <div
        className="mt-2.5 flex min-h-[80px] flex-col items-center justify-center gap-2 text-[11px] text-slate-500"
        role="status"
        aria-live="polite"
      >
        <span
          aria-hidden="true"
          className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-[#193A6A]"
        />
        Loading personas…
      </div>
    );
  }

  if (!personas.length) {
    return (
      <p className="mt-2.5 text-[11px] text-slate-400">
        {query ? `No persona matches "${query}".` : "No personas available."}
      </p>
    );
  }

  return (
    <div className="mt-2.5 flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
      {personas.map((persona) => (
        <button
          key={persona}
          type="button"
          onClick={() => onPick(persona)}
          className="h-fit rounded-md border border-[#193A6A]/20 bg-white px-2.5 py-1 font-mono text-[11px] font-medium text-[#193A6A] shadow-sm transition-colors hover:border-[#193A6A]/50 hover:bg-[#EEF5FF]"
        >
          {persona}
        </button>
      ))}
    </div>
  );
};

const PersonaBuilder = ({
  personas,
  isLoading,
  onAppend,
}: {
  personas: string[];
  isLoading: boolean;
  onAppend: (token: string) => void;
}) => {
  const [search, setSearch] = useState<string>("");
  const query = search.trim();

  const filteredPersonas = useMemo(
    () => personas.filter((persona) => matchesSearch(query, [persona])),
    [personas, query],
  );

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={sectionLabelClassName}>Operators</span>
        {PERSONA_OPERATORS.map((operator) => (
          <button
            key={operator}
            type="button"
            onClick={() => onAppend(operator)}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 font-mono text-[11px] font-semibold text-slate-600 shadow-sm transition-colors hover:border-[#193A6A]/40 hover:text-[#193A6A]"
          >
            {operator}
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <span className={sectionLabelClassName}>Personas</span>
        <SearchInput
          size="sm"
          value={search}
          onChange={setSearch}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.preventDefault();
            if (event.key === "Escape") setSearch("");
          }}
          placeholder="Search persona"
          ariaLabel="Search personas"
          clearLabel="Clear persona search"
        />
      </div>

      <PersonaList personas={filteredPersonas} isLoading={isLoading} query={query} onPick={onAppend} />
    </div>
  );
};

export const PersonaCombinationField = ({
  personas,
  isPersonaListLoading,
  validate,
}: {
  personas: string[];
  isPersonaListLoading: boolean;
  validate: (value: string) => true | string;
}) => {
  const { control, getValues, register, setFocus, setValue } = useFormContext<IHomeTileConfigFormFields>();
  const value = useWatch({ control, name: "persona_combination" }) ?? "";
  const { errors } = useFormState({ control, name: "persona_combination" });
  const error = errors.persona_combination?.message;

  const appendToken = (token: string) => {
    const currentValue = getValues("persona_combination") ?? "";
    const needsSpace = currentValue.length > 0 && !currentValue.endsWith("(") && token !== ")";
    setValue("persona_combination", `${currentValue}${needsSpace ? " " : ""}${token}`, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const clear = () => {
    setValue("persona_combination", "", { shouldValidate: true, shouldDirty: true });
    setFocus("persona_combination");
  };

  return (
    <>
      <label className={fieldLabelClassName} htmlFor="persona-combination">
        Persona Combination
      </label>
      <div className="relative">
        <input
          {...register("persona_combination", { validate })}
          id="persona-combination"
          type="text"
          placeholder="repeat AND (qcomm_yes OR appointmentuser) AND NOT member"
          className={`${textInputClassName} pr-9 font-mono text-xs ${error ? "border-rose-400" : ""}`}
        />
        {value.length > 0 && <ClearInputButton label="Clear persona combination" onClear={clear} />}
      </div>
      {error && <p className="mt-1.5 text-[11px] text-rose-600">{error}</p>}

      <PersonaBuilder personas={personas} isLoading={isPersonaListLoading} onAppend={appendToken} />
    </>
  );
};
